// React and MUI imports
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
// API utility
import Api from '../Api';
// Google Analytics
import ReactGA from 'react-ga4';
// Global state context
import { globalContext } from '../store';
import { Box, useTheme, useMediaQuery, styled } from '@mui/material';
// Notification banner and button
import HDNotificationBanner from '../hd-ui-kit/HDNotificationBanner';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HDButton from '../hd-ui-kit/HDButton';

// List of time zone options for dropdowns or selectors
export const TimeZoneOptions = [
  { label: '(GMT -12:00) Eniwetok, Kwajalein', id: '-12:00', abbreviation: 'MHT' },
  { label: '(GMT -11:00) Midway Island, Samoa', id: '-11:00', abbreviation: 'SMST' },
  { label: '(GMT -10:00) Hawaii', id: '-10:00', abbreviation: 'HAST' },
  { label: '(GMT -9:30) Taiohae', id: '-09:50', abbreviation: 'MART' },
  { label: '(GMT -9:00) Alaska', id: '-09:00', abbreviation: 'AK' },
  { label: '(GMT -8:00) Pacific Time (US & Canada)', id: '-08:00', abbreviation: 'PT' },
  { label: '(GMT -7:00) Mountain Time (US & Canada)', id: '-07:00', abbreviation: 'MT' },
  { label: '(GMT -6:00) Central Time (US & Canada), Mexico City', id: '-06:00', abbreviation: 'CT' },
  { label: '(GMT -5:00) Eastern Time (US & Canada), Bogota, Lima', id: '-05:00', abbreviation: 'ET' },
  { label: '(GMT -4:30) Caracas', id: '-04:50', abbreviation: 'VST' },
  { label: '(GMT -4:00) Atlantic Time (Canada), Caracas, La Paz', id: '-04:00', abbreviation: 'AST' },
  { label: '(GMT -3:30) Newfoundland', id: '-03:50', abbreviation: 'NST' },
  { label: '(GMT -3:00) Brazil, Buenos Aires, Georgetown', id: '-03:00', abbreviation: 'ART' },
  { label: '(GMT -2:00) Mid-Atlantic', id: '-02:00', abbreviation: 'MAST' },
  { label: '(GMT -1:00) Azores, Cape Verde Islands', id: '-01:00', abbreviation: 'AZOST' },
  { label: '(GMT) Western Europe Time, London, Lisbon, Casablanca', id: '+00:00', abbreviation: 'GMT' },
  { label: '(GMT +1:00) Brussels, Copenhagen, Madrid, Paris', id: '+01:00', abbreviation: 'RST' },
  { label: '(GMT +2:00) Kaliningrad, South Africa', id: '+02:00', abbreviation: '' },
  { label: '(GMT +3:00) Baghdad, Riyadh, Moscow, St. Petersburg', id: '+03:00', abbreviation: 'ARST' },
  { label: '(GMT +3:30) Tehran', id: '+03:50', abbreviation: 'IRST' },
  { label: '(GMT +4:00) Abu Dhabi, Muscat, Baku, Tbilisi', id: '+04:00', abbreviation: 'ARBST' },
  { label: '(GMT +4:30) Kabul', id: '+04:50', abbreviation: 'AFT' },
  { label: '(GMT +5:00) Ekaterinburg, Islamabad, Karachi, Tashkent', id: '+05:00', abbreviation: 'YEKT' },
  { label: '(GMT +5:30) Bombay, Calcutta, Madras, New Delhi', id: '+05:50', abbreviation: 'IST' },
  { label: '(GMT +5:45) Kathmandu, Pokhara', id: '+05:75', abbreviation: 'NPT' },
  { label: '(GMT +6:00) Almaty, Dhaka, Colombo', id: '+06:00', abbreviation: 'BST' },
  { label: '(GMT +6:30) Yangon, Mandalay', id: '+06:50', abbreviation: 'MYST' },
  { label: '(GMT +7:00) Bangkok, Hanoi, Jakarta', id: '+07:00', abbreviation: 'THA' },
  { label: '(GMT +8:00) Beijing, Perth, Singapore, Hong Kong', id: '+08:00', abbreviation: 'SGT' },
  { label: '(GMT +8:45) Eucla', id: '+08:75', abbreviation: 'ACWST' },
  { label: '(GMT +9:00) Tokyo, Seoul, Osaka, Sapporo, Yakutsk', id: '+09:00', abbreviation: 'TST' },
  { label: '(GMT +9:30) Adelaide, Darwin', id: '+09:50', abbreviation: 'CAUST' },
  { label: '(GMT +10:00) Eastern Australia, Guam, Vladivostok', id: '+10:00', abbreviation: '' },
  { label: '(GMT +10:30) Lord Howe Island', id: '+10:50', abbreviation: '' },
  { label: '(GMT +11:00) Magadan, Solomon Islands, New Caledonia', id: '+11:00', abbreviation: 'SBT' },
  { label: '(GMT +11:30) Norfolk Island', id: '+11:50', abbreviation: '' },
  { label: '(GMT +12:00) Auckland, Wellington, Fiji, Kamchatka', id: '+12:00', abbreviation: 'NZST' },
  { label: '(GMT +12:45) Chatham Islands', id: '+12:75', abbreviation: 'CHAST' },
  { label: '(GMT +13:00) Apia, Nukualofa', id: '+13:00', abbreviation: '' },
  { label: '(GMT +14:00) Line Islands, Tokelau', id: '+14:00', abbreviation: '' },
];

// Handles user redirection based on profile completion step
export const handleRerdiretion = (data: any, navigate: any) => {
  if (data?.step_completed == 3) {
    navigate('/create-profile/personal-details');
  } else if (data?.step_completed == 2) {
    navigate('/create-profile/compensation-details');
  } else if (data?.step_completed == 1) {
    navigate('/create-profile/experience');
  }
};

// Checks if user is authorized and redirects based on profile status
export const redirectionApi = (navigate: any) => {
  const isAuth = Api.isAuthorized();
  if (isAuth) {
    // authorized
    // check to see if user needs to complete profile
    Api.getAccount()
      .then((res) => {
        let data = res.response.data;
        handleRerdiretion(data, navigate);
      })
      .catch((err) => {
        console.log(err);
      });
  }
};

// Sends a Google Analytics event
export const reactGAEvents = (category: string, event: string) => {
  ReactGA.event({
    category: category,
    action: event,
  });
};

// Fetches and processes capability score data for a user
export const getCapabilityScore = async (assessmentId: string, roleId: string, profileData?: any, navigate?: any, isSuccessProfile?: boolean) => {  
  return await Api.getCapabilityScore(assessmentId, roleId, isSuccessProfile)
    .then(async (res) => {
      if (res?.success) {
        if (res?.response?.data?.message) {
          return { 'message': res?.response?.data?.message };
        } else {
          let capabilityRes: any = res.response.data;
          let count = 0;
          let calPercentage = 0;
          let percentage = 0;
          let skillList: any = [];
          let skillCardDetails: any = [];
          let skillCardDetailsKeys: any = [];
          let scoreDetailList: any = [];
          const capabilityResults = res?.response?.data?.results;
          Object.keys(capabilityResults)?.forEach(async (_key: any) => {
            let skillName = (_key.slice(0, 1).toUpperCase() + _key.slice(1, _key.length)).replace(/\b(\w)/g, (s: any) => s.toUpperCase());
            let score = (Math.round(capabilityResults[_key]?.benchmark_score * 20) > Math.round(capabilityResults[_key]?.user_score * 20)) ? 0 : 1;
            count = count + capabilityResults[_key]?.drive_factor_score;
            skillCardDetails.push({
              'driveFactorName': skillName,
              'score': score,
            });
            skillCardDetailsKeys[skillName] = {
              'driveFactorName': skillName,
              'score': score,
              '_key': _key,
              'value': capabilityResults[_key]
            };
          });
          scoreDetailList = await getCapabilityScoreCardDetails(skillCardDetails, skillCardDetailsKeys, profileData, navigate);
          skillList[0] = scoreDetailList;
          calPercentage = count / Object.keys(res.response?.data?.results).length;
          calPercentage = parseFloat(calPercentage.toFixed(1));
          percentage = Math.round(calPercentage);
          capabilityRes['percentage'] = isSuccessProfile ? Math.round(res?.response?.data?.matchScore) : percentage.toString();
          capabilityRes['LastAssessmentDate'] = res.response?.data?.last_assessment_date;
          capabilityRes['skills'] = skillList;
          return capabilityRes;
        }
      } else {
        return 'fail';
      }
    })
    .catch((err) => {
      console.log(err);
    });
};

// Fetches and processes impact score data for a user
export const getImpactScore = async (roleId: string, impactAssessmentId: string, profileData?: any, navigate?: any, isSuccessProfile?: boolean) => {
  return await Api.getImpactReport(roleId, impactAssessmentId,isSuccessProfile)
    .then((res) => {
      if (res?.success) {
        if (res?.response?.data?.message) {
          return 'errorMessage';
        } else {
          const reportData = res.response.data.results;
          let impactReports: any = [];
          let otherDetails: any = [];
          let locationParams: any = [];
          let coreSkillCounter = 1;
          let passedItems = 0;
          Object.keys(reportData).forEach((data: any) => {
            if (!impactReports[reportData[data].impact_area]) {
              locationParams[reportData[data].impact_area] = {};
              locationParams[reportData[data].impact_area]['title'] = reportData[data].impact_area;
              locationParams[reportData[data].impact_area]['description'] = reportData[data].impact_description;
              locationParams[reportData[data].impact_area]['roleName'] = profileData?.businessRole?.name;
              locationParams[reportData[data].impact_area]['skills'] = [];
              impactReports[reportData[data].impact_area] = {};
              impactReports[reportData[data].impact_area]['id'] = coreSkillCounter;
              impactReports[reportData[data].impact_area]['title'] = reportData[data].impact_area;
              impactReports[reportData[data].impact_area]['skills'] = [];
              impactReports[reportData[data].impact_area]['roleName'] = profileData?.businessRole?.name;
              impactReports[reportData[data].impact_area]['forwardClick'] = '';
              coreSkillCounter += 1;
            }
            impactReports[reportData[data].impact_area]['skills'].push({
              text: (data.slice(0, 1).toUpperCase() + data.slice(1, data.length)).replace(/^it/i, 'IT'),
              checked: Boolean(reportData[data].score),
              progress: reportData[data].user_score * 20,
              benchMark: reportData[data].benchmark_score * 20,
              description: reportData[data].statement_description,
              statementChecked: Boolean(reportData[data].user_score >= reportData[data].benchmark_score ? 1 : 0),
            });
            locationParams[reportData[data].impact_area]['skills'].push({
              text: (data.slice(0, 1).toUpperCase() + data.slice(1, data.length)).replace(/^it/i, 'IT'),
              checked: Boolean(reportData[data].user_score >= reportData[data].benchmark_score ? 1 : 0),
              description: reportData[data].statement_description,
              progress: reportData[data].user_score * 20,
              benchMark: reportData[data].benchmark_score * 20,
            });
            impactReports[reportData[data].impact_area]['state'] = { skillDetails: locationParams[reportData[data].impact_area] }
            impactReports[reportData[data].impact_area]['forwardClick'] = () => {
              reactGAEvents('my-reports-experience-overview', `impact-core-experience-${locationParams[reportData[data].impact_area]?.roleName}-${locationParams[reportData[data].impact_area]?.title}`);
              const route = '/experience/details';
              navigate(route, {
                state: { skillDetails: locationParams[reportData[data].impact_area] },
              });
            };
            passedItems += reportData[data].statement_score;
          });
          Object.keys(impactReports).forEach((data: any) => {
            impactReports[data]['skills'] = impactReports[data]['skills'].sort(
              (a: any, b: any) => b['checked'] - a['checked']
            );
          });
          let percentage = (passedItems / Object.keys(reportData).length);
          percentage = Math.round(parseFloat(percentage.toFixed(1)));
          otherDetails['percentage'] =  isSuccessProfile ? Math.round(res?.response?.data?.matchScore) : percentage.toString();
          otherDetails['LastAssessmentDate'] = res?.response?.data?.last_assessment_date;
          return { 'reportData': impactReports, 'otherDetails': otherDetails };
        }
      } else {
        return 'fail';
      }
    })
    .catch((err) => {
      console.log(err);
    });
};

// Helper to fetch and format skill card details for capability scores
const getCapabilityScoreCardDetails = async (skillCardDetails: any, skillCardDetailsKeys: any, data: any, navigate?: any) => {
  return await Api.getSkillScoreDetails(skillCardDetails)
    .then(async (response) => {
      if (response && response.response.status === 200) {
        if (response.response.data) {
          const cardDetailsData = response.response.data.map((item: any) => {
            return {
              title: item.driveFactorName,
              description: skillCardDetailsKeys[item.driveFactorName]['value']?.description,
              youPercent: Math.round(skillCardDetailsKeys[item.driveFactorName]['value']?.user_score * 20),
              rolePercent: Math.round(skillCardDetailsKeys[item.driveFactorName]['value']?.benchmark_score * 20),
              roleName: data?.businessRole?.name,
              userDetails: data,
              cardData: item.driveFactorResponse,
              state: {
                skillDetails: {
                  title: item.driveFactorName,
                  description: skillCardDetailsKeys[item.driveFactorName]['value']?.description,
                  youPercent: Math.round(skillCardDetailsKeys[item.driveFactorName]['value']?.user_score * 20),
                  rolePercent: Math.round(skillCardDetailsKeys[item.driveFactorName]['value']?.benchmark_score * 20),
                  roleName: data?.businessRole?.name,
                  cardsData: item.driveFactorResponse,
                },
                userDetails: data
              },
              forwardClick: () => {
                reactGAEvents('my-report-leadership-skills-overview', `capabilities-target--${data?.businessRole?.name}-${skillCardDetailsKeys[item.driveFactorName]['_key']}`);
                const route = '/leadership-skills/details';
                navigate(route, {
                  state: {
                    skillDetails: {
                      title: item.driveFactorName,
                      description: skillCardDetailsKeys[item.driveFactorName]['value']?.description,
                      youPercent: Math.round(skillCardDetailsKeys[item.driveFactorName]['value']?.user_score * 20),
                      rolePercent: Math.round(skillCardDetailsKeys[item.driveFactorName]['value']?.benchmark_score * 20),
                      roleName: data?.businessRole?.name,
                      cardsData: item.driveFactorResponse,
                    },
                    userDetails: data
                  },
                });
              },
            };
          });
          return cardDetailsData;
        }
      }
      return {};
    });
};

/**
 * LegendContent renders a legend for 'You' and 'target' in score visualizations.
 */
export const LegendContent = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { globalState } = useContext(globalContext);
  const WrapperYouAndBenchmark = styled(Box)(() => ({
    display: 'flex',
    justifyContent: isMobile ? 'flex-start' : 'flex-end',
    textAlign: 'left',
    alignItems: 'flex-start',
  }));

  const InnerWrapper = styled(Box)(() => ({
    display: 'flex',
  }));

  const RoleLegendWrapper = styled(Box)(() => ({
    height: '18px',
    width: isMobile ? '22px' : '19px',
    marginTop: '2px',
  }));

  const RoleLegend = styled(Box)(() => ({
    height: '100%',
    width: '18px',
    borderRadius: '50%',
    background: (isDark) ? '#fff' : '#0b0c10',
    border: '1px solid' + ((isDark) ? '#0b0c10' : 'transparent'),
    display: 'inline-block'
  }));

  const YouLegendWrap = styled(Box)(() => ({
    display: 'flex',
    marginTop: '2px',
  }));

  const YouLengendLeft = styled(Box)(() => ({
    background: (isDark) ? '#FEC350' : '#bc7d01',
    height: '16px',
    width: '8px',
    borderRadius: '150px 0 0 150px',
    marginRight: '1px',
  }));

  const YouLengendRight = styled(Box)(() => ({
    background: (isDark) ? '#2CDADA' : '#18768c',
    height: '16px',
    width: '8px',
    borderRadius: '0 150px 150px 0'
  }));

  const TypographyStyledYou = {
    fontFamily: 'Avenir',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    paddingLeft: '5px',
    paddingRight: '13px',
    color: isDark ? '#FFFFFF' : '#002136'
  };

  const TypographyStyledBenchMark = {
    fontFamily: 'Avenir',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    paddingLeft: '5px', color: isDark ? '#FFFFFF' : '#002136'
  };

  return (
    <WrapperYouAndBenchmark>
      <InnerWrapper>
        <YouLegendWrap>
          <YouLengendLeft>&nbsp;</YouLengendLeft>
          <YouLengendRight>&nbsp;</YouLengendRight>
        </YouLegendWrap>
        <Box sx={TypographyStyledYou}>You</Box>
      </InnerWrapper>
      <InnerWrapper>
        <RoleLegendWrapper>
          <RoleLegend>&nbsp;</RoleLegend>
        </RoleLegendWrapper>
        <Box sx={TypographyStyledBenchMark}>{globalState?.profile?.businessRole?.name} target</Box>
      </InnerWrapper>
    </WrapperYouAndBenchmark>
  );
};

// Background style for top left (light mode)
export const backgroundTopLeft = {
  background: 'radial-gradient(200% 70.42% at 6.02% 0%, #E3ECEF 0%, rgba(241, 241, 241, 0) 40%) #FFF',
  backgroundRepeat: 'no-repeat',
};

// Background style for top left (dark mode)
export const backgroundTopLeftDark = {
  background: 'radial-gradient(200% 70.42% at 6.02% 0%, #002136 0%, #002136 20.91%, rgba(7, 29, 45, 0.22) 35%, rgba(11, 12, 14, 0) 0%),#0B0C10',
  backgroundRepeat: 'no-repeat',
};


// Background style for top right (light mode)
export const backgroundTopRight = {
  background: 'radial-gradient(120vh 140vh at right 0%, #E3ECEF 60%, rgba(241, 241, 241, 0) 100%) #FFF',
  backgroundRepeat: 'no-repeat',
};

// Background style for top right (dark mode)
export const backgroundTopRightDark = {
  background: 'radial-gradient(120vh 140vh at right 0%, #002136 60%, #002136 0%, rgba(7, 29, 45, 0.22) 100%, rgba(11, 16, 14, 0) 130%),#0B0C10',
  backgroundRepeat: 'no-repeat',
};

// Background style for bottom (light mode)
export const backgroundBottom = {
  background: 'radial-gradient(200% 80.42% at 30.02% 100%, #E3ECEF 0%, rgba(241, 241, 241, 0) 70%) #FFF',
  backgroundRepeat: 'no-repeat',
};

// Background style for bottom (dark mode)
export const backgroundBottomDark = {
  background: 'radial-gradient(140.34% 70% at left bottom, #197E95 0%, #011E31 36.09%, #0B0C10 100%)',
  backgroundRepeat: 'no-repeat',
};

// Adds Google Analytics events for chemistry session based on current route
export const addGAEventsForChemistrySession = (gaTag: string) => {
  const urlList = [
    { '/home': 'home' },
    { '/experience/overview': 'experience-overview' },
    { '/experience/details': 'experience-details' },
    { '/leadership-skills/overview': 'leadership-skills-overview' },
    { '/leadership-skills/details': 'leadership-skills-details' }
  ];
  const currentPath = window.location.pathname;
  const matchingKey = urlList.find(item => currentPath === Object.keys(item)[0]);
  const category = matchingKey ? Object.values(matchingKey)[0] : '';
  reactGAEvents(category, gaTag);
};

// Fetches assessment status for the current user
export const getAssesmentStatus = async () => {
  return await Api.getAssesmentStatus()
    .then((res) => {
      if (res?.success) {
        const result = res?.response?.data;
        const statusArray = {
          cas: result?.capability_assessment_started,
          cac: result?.capability_assessment_completed,
          ias: result?.impact_assessment_started,
          iac: result?.impact_assessment_completed,
          otherDetails: result,
        };
        return statusArray;
      }
    })
    .catch((err) => {
      console.log(err);
    });
};

// Returns style object for titles based on device and theme
export const titleStyle = (isMobile: boolean, themeMode: string) => {
  return {
    fontFamily: 'Aventa',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: isMobile ? '31px' : '41px',
    lineHeight: isMobile ? '41px' : '54px',
    color: themeMode == 'light' ? '#002136' : '#FFFFFF',
    textAlign: 'left',
  };
};

// Returns style object for regular text based on theme
export const styledText = (themeMode: string) => {
  return {
    fontFamily: 'Avenir',
    fontStyle: 'normal',
    fontWeight: 300,
    fontSize: '16px',
    lineHeight: '24px',
    color: themeMode == 'light' ? '#334255' : '#CCCCCC',
    textAlign: 'left',
    marginTop: '15px',
  };
};

// Returns style object for sign-in button based on theme
export const styledSignInBtn = (themeMode: string) => {
  return {
    fontFamily: 'Aventa',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: themeMode == 'light' ? '#18768C' : '#2CDADA',
    padding: '0px',
    textDecoration: 'underline',
    ':hover': {
      color: themeMode == 'light' ? '#18768C' : '#2CDADA',
    },
  };
};

// Returns style object for main wrapper background based on device and theme
export const styledBoxMainWrapper = (isMobile: boolean, themeMode: string) => {
  let background =
    `url(res/SignInBG${themeMode === 'light' ? 'Light' : 'Dark'
    }${isMobile ? 'Mobile' : ''
    }.svg) no-repeat top`;
  return {
    background: background,
    backgroundPosition: isMobile ? 'right bottom' : 'right',
    height: isMobile ? '510px' : '570px',
  };
};

// Returns style object for left divider based on device and theme
export const styledDividerLeft = (isMobile: boolean, theme: any) => {
  return {
    borderLeft: !isMobile
      ? `1px solid rgba(${ResponsiveThemeValue(theme ,'0, 33, 54, 0.2' , '255, 255, 255, 0.2')})`
      : '',
    paddingLeft: isMobile ? ' ' : '40px',
    marginTop: isMobile ? '32px' : '64px',
    width: isMobile ? '100%' : '50%',
  };
};

// Exports a report as PDF and triggers download, with error handling
export const exportPDF = (setShowLoader: any, reportName: string, fileName: string, gaTags: any, setApiError: any) => {
  setShowLoader(true);
  reactGAEvents(gaTags?.category, gaTags?.tagName);
  const errMessage = 'We are having trouble while downloading your reports, please try again later.';
  return Api.exportPDF(reportName)
    .then((res) => {
      if (res && res?.success) {
        setShowLoader(false);
        if (res?.response?.data?.report) {
          const linkSource = `data:application/pdf;base64,${res.response.data.report}`;
          const downloadLink = document.createElement('a');
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          downloadLink.click();
        } else {
          setApiError(errMessage);
          setTimeout(() => {
            setApiError('');
          }, 5000);
        }

      } else {
        setShowLoader(false);
        setApiError(errMessage);
        setTimeout(() => {
          setApiError('');
        }, 5000);
      }
    })
    .catch((err) => {
      console.log(err);
    });
};

// Returns style object for export button based on device and theme
export const exportBtnStyle = (isMobile: boolean, themeMode: string) => {
  return {
    padding: 0,
    display: isMobile ? 'none' : 'flex',
    fontWeight: 500,
    '&:focus': {
      border: 'none'
    },
    color: themeMode == 'light' ? '#18768C' : '#66FCFC',
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    }
  };
};

// Returns style object for details button based on theme
export const detailsBtnStyle = (themeMode: string) => {
  return {
    fontFamily: 'Avenir',
    fontWeight: 600,
    fontSize: '16px',
    padding: 0,
    textWrap: 'nowrap',
    display: 'flex',
    color: themeMode == 'light' ? '#18768C' : '#66FCFC',
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  };
};

// Navigates to a given path with state
export const handleForward = (path: string, navigate: any, state: any) => {
  const route = path;//'/leadership-skills/details';
  navigate(route, {
    state
  });
};

/**
 * GenerateFooterSocialIcons renders styled social media icons for the footer.
 */
export const GenerateFooterSocialIcons = (props: any) => {
  const StyledBoxIconsWrapper = styled(Box)(() => ({
    display: 'flex',
    width: (props?.theme) ? '100%' : '136px',
    alignItems: 'center',
    justifyItems: 'center',
    justifyContent: (props?.theme) ? 'end' : 'flex-start',
    marginTop: '3px',
    flexDirection: (props?.theme) ? 'unset' : 'row',
    padding: '0px',
    gap: (props?.theme) ? 'auto' : '20px',
    height: (props?.theme) ? 'auto' : '24px',
  }));

  const styleIconDeskProps = {
    [props?.theme?.breakpoints.down('md')]: { marginRight: '4px' },
    marginRight: '29px',
  };

  let styledIconArray = {
    color: '#CCCCCC'
  };

  if (props?.theme) {
    styledIconArray = { ...styledIconArray, ...styleIconDeskProps };
  }

  const StyledLinkedInIcon = styled(props?.LinkedInIcon)(() => ({
    ...styledIconArray
  }));

  const StyledTwitterIcon = styled('img')(() => ({
    color: '#CCCCCC',
    [props?.theme?.breakpoints.down('md')]: { marginRight: '4px' },
  }));

  const StyledYouTubeIcon = styled(props?.YouTubeIcon)(() => ({
    ...styledIconArray
  }));

  return (
    <StyledBoxIconsWrapper>
      <Box>
        <a href={props?.urls?.linkedInUrl} target='_blank'>
          <StyledLinkedInIcon />
        </a>
      </Box>
      <Box>
        <a href={props?.urls?.youtubeUrl} target='_blank'>
          <StyledYouTubeIcon />
        </a>
      </Box>
      <Box>
        <a href={props?.urls?.twitterUrl} target='_blank'>
          <StyledTwitterIcon src={window.location.origin + '/res/email-template-images/twitter-logo.svg'} alt='twitter' />
        </a>
      </Box>
    </StyledBoxIconsWrapper>
  );
};

// Returns value based on mobile device check
export const ResponsiveMobileValue = (isMobile: any, mobileValue: any, otherValue: any) => {
  return isMobile ? mobileValue : otherValue;
};

// Returns value based on tablet device check
export const ResponsiveTabletValue = (isTablet: any, tabletValue: any, otherValue: any) => {
  return isTablet ? tabletValue : otherValue;
};

// Returns value based on current theme (light/dark)
export const ResponsiveThemeValue = (theme: any, lightTheme: any, darkTheme: any) => {
  return theme.palette.mode === 'light' ? lightTheme : darkTheme;
};

// Returns value based on mobile or tablet device check
export const ResponsiveMobileTabletValue = (isMobileTabletValue: any, mobileTabletValue: any, otherValue: any) => {
  return isMobileTabletValue ? mobileTabletValue : otherValue;
};

// Returns value based on large tablet device check
export const ResponsiveTabletLargeValue = (isTabletLargeValue: any, tabletLargeValue: any, otherValue: any) => {
  return isTabletLargeValue ? tabletLargeValue : otherValue;
};

// Returns value based on mobile or large tablet device check
export const ResponsiveMobileTabletLargeValue = (isMobileTabletLargeValue: any, mobileTabletLargeValue: any, otherValue: any) => {
  return isMobileTabletLargeValue ? mobileTabletLargeValue : otherValue;
};

// Returns a dynamic CSS value based on device and other conditions
export const getDynamicCss = (isMobile:any, mobileVal:any, otherCheck:any, otherVal:any, defaultVal:any)=>{
  if (isMobile) return mobileVal;
  if (otherCheck) return otherVal;
  return defaultVal;
};


export const isDemographicApplicable = (selectedCountry: string) => {
    if (['Canada', 'United States of America', 'United Kingdom', 'Australia'].includes(selectedCountry)) {
      return true;
    } else {
      return false;
    }
  };
